import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Euro, ShoppingCart, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Hero = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const scrollToTarifs = () => {
    document.getElementById('tarifs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative lg:min-h-[92vh] flex flex-col lg:flex-row lg:items-center overflow-hidden bg-gray-950" data-testid="hero-section">
      <div className="relative h-[50vh] min-h-[340px] lg:absolute lg:inset-0 lg:h-auto lg:min-h-0">
        <picture>
          <source media="(max-width: 767px)" srcSet="/hero-camion-ler-900.webp" type="image/webp" />
          <source srcSet="/hero-camion-ler.webp" type="image/webp" />
          <img
            src="/hero-camion-ler.jpg"
            alt="Camion multichaîne LER avec benne rouge - Location de bennes en Île-de-France"
            className="w-full h-full object-cover object-[62%_45%] lg:object-[center_45%]"
            fetchpriority="high"
            loading="eager"
            decoding="async"
            data-testid="hero-background-image"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/50 via-gray-950/0 to-gray-950 lg:bg-gradient-to-r lg:from-gray-950/90 lg:via-gray-950/60 lg:to-gray-950/5" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 pb-12 lg:mt-0 lg:pt-32 lg:pb-20">
        <div className="max-w-2xl text-left space-y-6 lg:space-y-7">
          <div className="space-y-3">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-white" data-testid="hero-title">
              Location de bennes en Île-de-France
            </h1>
            <p className="text-base md:text-lg font-semibold text-green-400" data-testid="hero-subtitle">
              L'expérience du terrain au service de vos chantiers
            </p>
          </div>

          <p className="text-sm sm:text-base text-gray-200 leading-relaxed" data-testid="hero-text">
            Nous accompagnons les professionnels et les particuliers dans la location de bennes et l’évacuation
            de leurs déchets en Île-de-France. Du choix du volume à l’enlèvement, nous organisons chaque étape
            selon la nature de vos déchets, les contraintes d’accès et le calendrier de votre chantier.
            Un interlocuteur dédié, des tarifs transparents et une prise en charge des déchets en centre agréé
            pour vos travaux de rénovation, de démolition et de terrassement.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button
              onClick={scrollToTarifs}
              size="lg"
              className="bg-green-600 hover:bg-green-500 text-white py-6 px-8 text-lg font-bold shadow-xl shadow-green-900/40 transition-colors"
              data-testid="hero-tarifs-btn"
            >
              <Euro className="mr-2 h-5 w-5" />
              Voir les tarifs
            </Button>
            {user ? (
              <Button
                onClick={() => navigate('/mon-compte')}
                size="lg"
                className="bg-white hover:bg-gray-100 text-gray-900 py-6 px-8 text-lg font-bold shadow-xl transition-colors"
                data-testid="hero-mon-compte-btn"
              >
                <User className="mr-2 h-5 w-5" />
                Accéder à mon compte
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/commander-sans-compte')}
                size="lg"
                className="bg-white hover:bg-gray-100 text-gray-900 py-6 px-8 text-lg font-bold shadow-xl transition-colors"
                data-testid="hero-commander-sans-compte-btn"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Commander sans compte
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>

          {user ? (
            <p className="text-sm text-gray-300">
              Connecté en tant que <span className="font-bold text-green-400">{user.name}</span>
            </p>
          ) : (
            <p className="text-sm text-gray-300">
              Professionnel ?{' '}
              <button
                onClick={() => navigate('/inscription')}
                className="text-white font-bold underline underline-offset-4 hover:text-green-400 transition-colors"
                data-testid="hero-inscription-btn"
              >
                Créer un compte
              </button>{' '}
              pour vos prix en temps réel ·{' '}
              <button
                onClick={() => navigate('/connexion')}
                className="text-white font-bold underline underline-offset-4 hover:text-green-400 transition-colors"
                data-testid="hero-login-link"
              >
                Se connecter
              </button>
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
